import { useState, useEffect } from 'react';
import { X, Plus, Link as LinkIcon, FileText, AlertTriangle, Euro } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AddFundCallLinkModal } from './AddFundCallLinkModal';

interface Invoice {
  id: string;
  supplier: string;
  invoice_number: string;
  invoice_date: string;
  total_amount: number;
  description: string;
  status: string;
  notes: string;
}

interface FundCallLink {
  id: string;
  fund_call_id: string;
  allocated_amount: number;
  justification_note: string;
  subsidy_name: string;
  fund_call_name: string;
}

interface InvoiceDetailModalProps {
  invoice: Invoice;
  onClose: () => void;
  onUpdate: () => void;
}

export function InvoiceDetailModal({ invoice, onClose, onUpdate }: InvoiceDetailModalProps) {
  const [fundCallLinks, setFundCallLinks] = useState<FundCallLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddLinkModal, setShowAddLinkModal] = useState(false);

  useEffect(() => {
    loadFundCallLinks();
  }, [invoice.id]);

  const loadFundCallLinks = async () => {
    try {
      const { data, error } = await supabase
        .from('grant_fund_call_invoices')
        .select(`
          id,
          fund_call_id,
          allocated_amount,
          justification_note,
          fund_call:grant_fund_calls(
            name,
            subsidy:grant_subsidies(name)
          )
        `)
        .eq('invoice_id', invoice.id);

      if (error) throw error;

      const links = (data || []).map((link: any) => ({
        id: link.id,
        fund_call_id: link.fund_call_id,
        allocated_amount: link.allocated_amount,
        justification_note: link.justification_note,
        fund_call_name: link.fund_call.name,
        subsidy_name: link.fund_call.subsidy.name,
      }));

      setFundCallLinks(links);
    } catch (error) {
      console.error('Error loading fund call links:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLink = async (linkId: string) => {
    if (!confirm('Voulez-vous vraiment retirer ce lien ?')) return;

    try {
      const { error } = await supabase
        .from('grant_fund_call_invoices')
        .delete()
        .eq('id', linkId);

      if (error) throw error;
      loadFundCallLinks();
    } catch (error) {
      console.error('Error removing link:', error);
      alert('Erreur lors de la suppression du lien');
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  const totalAllocated = fundCallLinks.reduce((sum, link) => sum + (link.allocated_amount || 0), 0);
  const hasOverAllocation = totalAllocated > invoice.total_amount;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{invoice.supplier}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {invoice.invoice_number && `N° ${invoice.invoice_number} • `}
              {formatDate(invoice.invoice_date)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Invoice info */}
          <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-600">Montant total</div>
              <div className="text-2xl font-bold text-gray-900 flex items-center gap-2 mt-1">
                <Euro className="w-6 h-6" />
                {formatCurrency(invoice.total_amount)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Statut</div>
              <div className="mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  invoice.status === 'complet'
                    ? 'bg-green-100 text-green-800'
                    : invoice.status === 'incomplet'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-orange-100 text-orange-800'
                }`}>
                  {invoice.status === 'complet' ? 'Complet' : invoice.status === 'incomplet' ? 'Incomplet' : 'À vérifier'}
                </span>
              </div>
            </div>
            {invoice.description && (
              <div className="col-span-2">
                <div className="text-sm text-gray-600 mb-1">Description</div>
                <div className="text-sm text-gray-900">{invoice.description}</div>
              </div>
            )}
            {invoice.notes && (
              <div className="col-span-2">
                <div className="text-sm text-gray-600 mb-1">Notes</div>
                <div className="text-sm text-gray-900">{invoice.notes}</div>
              </div>
            )}
          </div>

          {/* Allocation warning */}
          {hasOverAllocation && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <div className="text-sm text-orange-800">
                <strong>Attention:</strong> Le total des montants affectés ({formatCurrency(totalAllocated)})
                dépasse le montant de la facture ({formatCurrency(invoice.total_amount)})
              </div>
            </div>
          )}

          {/* Total allocated */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-sm text-blue-800 font-medium">Total affecté</div>
            <div className="text-2xl font-bold text-blue-900 mt-1 flex items-center gap-2">
              <Euro className="w-6 h-6" />
              {formatCurrency(totalAllocated)}
            </div>
            {invoice.total_amount - totalAllocated > 0 && (
              <div className="text-sm text-blue-700 mt-1">
                Reste à couvrir: {formatCurrency(invoice.total_amount - totalAllocated)}
              </div>
            )}
          </div>

          {/* Fund call links */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Financement (ADF liés)</h3>
              <button
                onClick={() => setShowAddLinkModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Ajouter un lien ADF
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto"></div>
              </div>
            ) : fundCallLinks.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <LinkIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-3">Aucun ADF lié à cette facture</p>
                <button
                  onClick={() => setShowAddLinkModal(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Lier à un ADF
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {fundCallLinks.map((link) => (
                  <div
                    key={link.id}
                    className="p-4 bg-white border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-emerald-600" />
                          <div>
                            <div className="font-medium text-gray-900">{link.subsidy_name}</div>
                            <div className="text-sm text-gray-600">{link.fund_call_name}</div>
                          </div>
                        </div>
                        <div className="mt-3 pl-8">
                          <div className="text-xs text-gray-600">Montant affecté</div>
                          <div className="text-lg font-semibold text-emerald-600">{formatCurrency(link.allocated_amount)}</div>
                          {link.justification_note && (
                            <div className="mt-2 text-sm text-gray-600">
                              <span className="font-medium">Note:</span> {link.justification_note}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveLink(link.id)}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        Retirer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>

      {/* Modal */}
      {showAddLinkModal && (
        <AddFundCallLinkModal
          invoiceId={invoice.id}
          invoiceSupplier={invoice.supplier}
          remainingAmount={invoice.total_amount - totalAllocated}
          onClose={() => setShowAddLinkModal(false)}
          onSuccess={() => {
            setShowAddLinkModal(false);
            loadFundCallLinks();
          }}
        />
      )}
    </div>
  );
}
